/* globals ca */
import { has } from 'lodash';
import pluralize from 'pluralize';
import { Media, MediaSet } from 'ca-api-client';

export const setState = {
    mediaset: {
        item: {},
        selected: [],
        selectedCount: 0,
        selectedFilesize: 0,
        isLoading: false,
    },
};

export const setMutations = {
    setMediaSetItem(state, item) {
        state.mediaset.item = item;
    },
    setMediaSetMedia(state, media) {
        state.mediaset.item.mediaItems = media;
    },
    setMediaSetSelected(state, selected) {
        state.mediaset.selected = selected;
        state.mediaset.selectedCount = state.mediaset.selected.length;
    },
    setMediaSetSelectedItemsOld(state, item) {
        if (item.checked && !state.mediaset.selected.includes(item.id)) {
            state.mediaset.selected.push(item);
        } else {
            state.mediaset.selected = state.mediaset.selected.filter((e) => e !== item.id);
        }
        state.mediaset.selectedCount = state.mediaset.selected.length;

        const totalSize = state.mediaset.selected.map((item) => item.size).reduce((sum, current) => sum + current, 0);

        state.mediaset.selectedFilesize = totalSize;
    },
    setMediaSetSelectedItems(state, item) {
        const selected = state.mediaset.selected.map((i) => i.id);
        if (item.checked && !selected.includes(item.id)) {
            state.mediaset.selected.push(item);
        } else {
            const filteredArray = state.mediaset.selected.filter((e) => e.id !== item.id);
            state.mediaset.selected = filteredArray;
        }
        state.mediaset.selectedCount = state.mediaset.selected.length;

        const totalSize = state.mediaset.selected.map((item) => item.size).reduce((sum, current) => sum + current, 0);

        state.mediaset.selectedFilesize = totalSize;
    },
};

export const setGetters = {
    getMediaSetId(state) {
        return state.mediaset.item.id;
    },
    getMediaSetItem(state) {
        return state.mediaset.item;
    },
    getMediaSetMedia(state) {
        return state.mediaset.item.mediaItems;
    },
    getMediaSetSets(state) {
        return state.mediaset.item.sets;
    },
    getMediaSetMediaById: (state) => (id) => {
        // Set current items position in gallery
        const item = state.mediaset.item.media.data.find((item) => item.id === id);
        return item;
    },
    getMediaSetMediaIds(state) {
        let ids = [];
        if (has(state.mediaset.item, 'media') && has(state.mediaset.item.media, 'data')) {
            ids = state.mediaset.item.mediaIds;
        }
        return ids;
    },
    getMediaSetSelected(state) {
        return state.mediaset.selected;
    },
    getMediaSetSelectedCount(state) {
        return state.mediaset.selectedCount;
    },
    getMediaSetSelectedIds(state) {
        return state.mediaset.selected.map((item) => item.id);
    },
    getMediaSetSelectedFilesize(state) {
        return state.mediaset.selectedFilesize;
    },
    getMediaSetSelectedByType(state) {
        const selectedTypes = {};
        const selected = state.mediaset.selected;

        const media = selected.filter((item) => item.type !== 'gallery').map((item) => item.id);
        selectedTypes.media = media;

        const sets = selected.filter((item) => item.type === 'gallery').map((item) => item.id);
        selectedTypes.sets = sets;

        return selectedTypes;
    },
};

export const setActions = {
    replaceMediaSetItem({ commit, dispatch }, item) {
        commit('setMediaSetItem', item);
        dispatch('replaceGalleryMediaSetItem', item);
    },

    removeMediaSetItem({ commit, state }, id) {
        return new Promise((resolve) => {
            if (!Array.isArray(id)) id = [id];

            if (state.mediaset.item.mediaSets.length > 0) {
                const mediaSets = state.mediaset.item.mediaSets.filter((item) => !id.includes(item.id));
                state.mediaset.item.mediaSets = mediaSets;
            }

            if (state.mediaset.item.mediaItems.length > 0) {
                const mediaItems = state.mediaset.item.mediaItems.filter((item) => !id.includes(item.id));
                commit('setMediaSetMedia', mediaItems);
            }
            resolve();
        });
    },

    async createMediaSet({ dispatch }, payload) {
        const selected = payload.selected || [];
        const title = payload.title || 'New collection';
        const media = selected.filter((i) => i.type !== 'gallery').map((i) => i.id);
        const children = selected.filter((i) => i.type === 'gallery').map((i) => i.id);

        const params = {
            title,
            project_id: ca.project.uuid,
            type: 'gallery',
        };

        if (media.length > 0) params.media = media;
        if (children.length > 0) params.children = children;
        if (payload.parent) params.parent_id = payload.parent;

        const set = new MediaSet(params);

        const newSet = await set.save();

        // was added as a child fetch parent set
        if (payload.parent) {
            dispatch('fetchMediaSet', payload.parent);
        }

        return newSet;
    },

    async fetchMediaSet({ commit }, id) {
        const set = await MediaSet.params({ include: 'children,parent' }).$find(id);
        commit('setMediaSetItem', set);
        return set;
    },

    async fetchMediaSets({ commit }, params) {
        return await MediaSet.params(params).get();
    },

    async updateSet({ state }, payload) {
        const id = payload.id;
        const data = payload.data || {};
        const set = await MediaSet.params({ include: 'children' }).$find(id);

        // Add new items at beginning of existing items
        if (has(data, 'media') && data.media && !payload.reorder) {
            const media = set.mediaIds;
            const mediaToAdd = data.media.filter((item) => !media.includes(item));
            data.media = mediaToAdd.concat(media);
        }

        if (has(data, 'children') && data.children && !payload.reorder) {
            const children = set.childIds;
            const setsToAdd = data.children.filter((item) => !children.includes(item));
            data.children = setsToAdd.concat(children);
        }

        const update = await set._update(data);
        update.mediaItems = update.media.data;
        update.mediaSets = update.children.data;

        return update;
    },

    async removeSetParent({ state }, id) {
        const set = await MediaSet.params({ include: 'parent' }).$find(id);
        return await set._update({ parent_id: null });
    },

    async addToExistingSet({ state, getters }, payload) {
        const id = payload.id;
        const set = await MediaSet.$find(id);
        const data = { id };

        if (payload.items.length > 0) {
            const setItems = set.media.data.map((item) => item.id);
            data.media = [].concat(payload.items, setItems);
        }
        const update = await set._update(data);
        update.mediaItems = update.media.data;

        return update;
    },

    async removeItemFromSet({ commit, getters }, id) {
        if (!Array.isArray(id)) id = [id];
        const setId = getters.getMediaSetId;
        const set = await MediaSet.params({ include: 'children' }).$find(setId);
        const media = set.mediaIds.filter((i) => !id.includes(i));
        const children = set.childIds.filter((i) => !id.includes(i));
        const update = await set._update({ media, children });
        update.mediaItems = update.media.data;
        update.mediaSets = update.children.data;
        return update;
    },

    async deleteSetItem({ commit, dispatch }, id) {
        let mediaSet = null;
        if (Array.isArray(id)) {
            mediaSet = new MediaSet();
        } else {
            mediaSet = await MediaSet.$find(id);
        }

        return await mediaSet.delete(id).catch(async (error) => {
            if (error.response.status === 409) {
                let itemCount = 1;
                const errorData = error.response.data.data;
                if (Array.isArray(id)) itemCount = id.length;

                // titles cannot use line breaks as swal2 converts to <br>
                const title = `<span class="text-danger">By deleting ${pluralize('this', itemCount)} ${pluralize(
                    'collection',
                    itemCount
                )} you will be removing/deleting the following:</span>`;

                await ca.confirm(
                    {
                        title,
                        type: 'delete',
                        html: mediaSet.createDependencyListForDelete(id, errorData),
                        params: [errorData],
                    },
                    async (errorData) => {
                        return await mediaSet.delete(id, true).then((response) => {
                            dispatch('removeDeletedItems', id);
                        });
                    }
                );
            }
        });
    },

    async deleteMediaSet({ commit }, id) {
        const mediaSet = await MediaSet.$find(id);
        return await mediaSet.delete(id);
    },

    async fetchMediaSetTree({ dispatch }) {
        const sets = await dispatch('fetchMediaSets', {
            include: 'children',
            filter: { parent_id: null },
            limit: -1,
        });
        return sets;
    },

    async updateManySets({ dispatch }, payload) {
        const mediaSet = new MediaSet();
        return await mediaSet.updateMany(payload);
    },

    replaceMediaSetMediaItem({ commit, state }, media) {
        if (!(media instanceof Media)) {
            media = new Media(media);
        }

        const mediaItems = state.mediaset.item.mediaItems;

        const itemIndex = mediaItems.findIndex(item => item.id === media.id);

        if (itemIndex > -1) {
            mediaItems.splice(itemIndex, 1, media);
        }
    },

    clearMediaSetSelected({ commit }) {
        commit('setMediaSetSelected', []);
    },
};
